import Konva from 'konva';
import Response from 'services/graphql/response';
import ResponseError from 'services/graphql/response-error';
import { makeVar, useReactiveVar } from '@apollo/client';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useOnSurfaceChange from 'routes/surface/hooks/use-on-surface-change';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import { useLoggedUser } from 'state/user';
import { get } from 'lodash';
import { Position } from 'types';
import { difference, notEmpty } from 'utils';

const ScaleBy = 0.05;

export interface CanvasStage {
  id: string;
  x: number;
  y: number;
  scale: number;
  scaleBy: number;
}

export interface CanvasStageInput {
  id?: string;
  x?: number;
  y?: number;
  scale?: number;
}

type UpdateCanvasStage = (
  input: CanvasStageInput,
) => Promise<Response | ResponseError>;

export const stageVar = makeVar<CanvasStage>(getDefaultStageData(''));
export const stageScaleVar = makeVar<number>(1);
export const stagePositionVar = makeVar<Position>({ x: 0, y: 0 });

const STAGES_KEY = 'cortlex-stages';

export function updateCanvasStageInLocalStorage(
  id: string,
  userId: string,
): UpdateCanvasStage {
  return function (input: CanvasStageInput): Promise<Response | ResponseError> {
    return new Promise((resolve, reject) => {
      try {
        const previousStage = getCanvasStageById(id, userId);

        const newStage = { ...previousStage, ...input, id };

        const stages = getAllStages();

        stages[getStageKey(id, userId)] = newStage;

        localStorage.setItem(STAGES_KEY, JSON.stringify(stages));

        resolve(new Response(getCanvasStageById(id, userId)));
      } catch (error) {
        console.error('------- updateCanvasStage -------------- ');
        reject(new ResponseError(error));
      }
    });
  };
}

export function updateCanvasStage(
  id: string,
  userId: string,
  stageInstance: Konva.Stage | undefined,
): UpdateCanvasStage {
  return function (input: CanvasStageInput): Promise<Response | ResponseError> {
    return new Promise((resolve, reject) => {
      try {
        const previousStage = getCanvasStageById(id, userId);

        const newStage = { ...previousStage, ...input, id };

        //stageVar(newStage);

        const differenceInValues = difference(previousStage, newStage) as any;

        if (notEmpty(differenceInValues)) {
          if (differenceInValues?.scale) {
            stageScaleVar(newStage?.scale);
          }

          if (differenceInValues.x || differenceInValues.y) {
            stagePositionVar({
              x: newStage.x,
              y: newStage.y,
            });
          }
        }

        stageInstance?.x(newStage.x);
        stageInstance?.y(newStage.y);
        stageInstance?.scaleX(newStage.scale);
        stageInstance?.scaleY(newStage.scale);

        const stages = getAllStages();

        stages[getStageKey(id, userId)] = newStage;

        localStorage.setItem(STAGES_KEY, JSON.stringify(stages));

        resolve(new Response(getCanvasStageById(id, userId)));
      } catch (error) {
        console.error('------- updateCanvasStage -------------- ');
        reject(new ResponseError(error));
      }
    });
  };
}

export function useCanvasStage(id: string): [CanvasStage, UpdateCanvasStage] {
  const { stageInstance } = useCanvas();

  const [loggedUser] = useLoggedUser();

  const stage = stageInstance
    ? {
        id,
        x: stageInstance?.x(),
        y: stageInstance?.y(),
        scale: stageInstance?.scaleX(),
        scaleBy: ScaleBy,
      }
    : getCanvasStageById(id, loggedUser?.id as string);

  useOnSurfaceChange((surface) => {
    if (surface) {
      const stageData = getCanvasStageById(surface, loggedUser?.id as string);

      stageInstance?.x(stageData.x);
      stageInstance?.y(stageData.y);
      stageInstance?.scaleX(stageData.scale);
      stageInstance?.scaleY(stageData.scale);

      stageScaleVar(stageData.scale);
      stagePositionVar({ x: stageData.x, y: stageData.y });
    }
  });

  return [
    { ...stage, scaleBy: ScaleBy },
    updateCanvasStage(id, loggedUser?.id as string, stageInstance),
  ];
}

export function useCanvasStageOperations(
  id?: string,
): {
  getCanvasStage: () => CanvasStage;
  updateCanvasStage: UpdateCanvasStage;
  updateCanvasStageInLocalStorage: UpdateCanvasStage;
  getCanvasStageById(id: string): CanvasStage;
} {
  const currentSurface = useCurrentSurface();
  id = (id ?? currentSurface?.id) as string;

  const { stageInstance } = useCanvas();
  const [loggedUser] = useLoggedUser();

  return {
    updateCanvasStage: updateCanvasStage(
      id,
      loggedUser?.id as string,
      stageInstance,
    ),
    updateCanvasStageInLocalStorage: updateCanvasStageInLocalStorage(
      id,
      loggedUser?.id as string,
    ),
    getCanvasStage: () => {
      return getCanvasStageById(id as string, loggedUser?.id as string);
    },
    getCanvasStageById: (id: string) =>
      getCanvasStageById(id, loggedUser?.id as string),
  };
}

export function useCanvasStageScale(): number {
  return useReactiveVar(stageScaleVar);
}

export function useCanvasStagePosition(): Position {
  return useReactiveVar(stagePositionVar);
}

function getDefaultStageData(id: string): any {
  return { id, x: 0, y: 0, scale: 0.75, scaleBy: ScaleBy };
}

function getStageKey(stageId: string, userId: string): string {
  return userId + '-' + stageId;
}

function getAllStages() {
  try {
    const stagesString = localStorage.getItem(STAGES_KEY);
    if (stagesString) {
      return JSON.parse(stagesString);
    }
    return {};
  } catch {
    return {};
  }
}

function getCanvasStageById(id: string, userId: string) {
  try {
    const stagesString = localStorage.getItem(STAGES_KEY);

    if (stagesString) {
      const stages = JSON.parse(stagesString);

      const stage = get(
        stages,
        getStageKey(id, userId),
        getDefaultStageData(id),
      );

      stage.scaleBy = ScaleBy;

      return stage;
    }

    throw new Error('Stage position by id not found');
  } catch (error) {
    return getDefaultStageData(id);
  }
}
